import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Group,
  Paper,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconBook,
  IconChevronDown,
  IconChevronUp,
  IconCopy,
  IconExternalLink,
  IconSearch,
  IconStar,
  IconX,
} from "@/components/icons";
import {
  KnowledgeArticleContent,
  stripKnowledgeContent,
} from "@/components/knowledge/KnowledgeArticleContent";
import { KnowledgeArticleAttachments } from "@/components/knowledge/KnowledgeArticleAttachments";
import { HighlightedSearchText } from "@/components/knowledge/HighlightedSearchText";
import {
  scoreLocalSearchMatch,
  snippetAnchorTokens,
  meaningfulSearchTokens,
} from "@/components/knowledge/search-utils";
import {
  FALLBACK_MATERIAL_TYPES,
  MATERIAL_TYPE_COLORS,
  MATERIAL_TYPE_LABELS,
  articlePreviewText,
  articleReadyReplyText,
  countByType,
} from "@/components/knowledge/knowledge-base-shared";
import "@/components/knowledge/knowledge-content.css";
import { FloatingWorkspacePanel, PageCenterLoader } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { showNotification } from "@mantine/notifications";
import type {
  SupportKnowledgeArticleRow,
  SupportKnowledgeMaterialType,
  SupportKnowledgeSearchRow,
  SupportKnowledgeVariableDefinitionRow,
} from "@/types/api";

type Props = {
  ticketId: string;
  operatorId?: string | null;
  projectId: string;
  projectName?: string | null;
  onInsertToReply: (text: string) => void;
  opened: boolean;
  onOpenedChange: (opened: boolean) => void;
};

type ScopeFilter = "all" | "general" | "favorites";
type TypeFilter = "all" | SupportKnowledgeMaterialType;
type SearchMode = "semantic" | "exact" | "title" | "tags";

type TicketKnowledgePanelState = {
  query?: string;
  searchMode?: SearchMode;
  scopeFilter?: ScopeFilter;
  typeFilter?: TypeFilter;
  selectedId?: string | null;
  showFullDocument?: boolean;
  controlsCollapsed?: boolean;
};

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

type ListItem = SupportKnowledgeSearchRow | SupportKnowledgeArticleRow;

function isSearchRow(
  item: ListItem | null | undefined,
): item is SupportKnowledgeSearchRow {
  return (
    !!item &&
    "relevance_score" in item &&
    typeof item.relevance_score === "number"
  );
}

function typeLabel(item: ListItem): string {
  return (
    item.material_type_label ||
    MATERIAL_TYPE_LABELS[item.material_type] ||
    item.material_type
  );
}

function scopeLabel(item: ListItem, projectName?: string | null): string {
  if (!item.project_id) return "Общие";
  return projectName || "Проект";
}

function filterByScope(
  items: ListItem[],
  scope: ScopeFilter,
  favoriteIds: Set<string>,
): ListItem[] {
  if (scope === "favorites") return items.filter((i) => favoriteIds.has(i.id));
  if (scope === "general") return items.filter((i) => !i.project_id);
  return items;
}

function normalizeScopeFilter(value: unknown): ScopeFilter {
  if (value === "favorites") return "favorites";
  return value === "general" ? "general" : "all";
}

function normalizeSearchMode(value: unknown): SearchMode {
  if (value === "exact" || value === "title" || value === "tags") return value;
  return "semantic";
}

function filterByType(items: ListItem[], typeKey: TypeFilter): ListItem[] {
  if (typeKey === "all") return items;
  return items.filter((i) => (i.material_type || "other") === typeKey);
}

function makeLocalSnippet(text: string, query: string, maxLen = 280): string {
  const plain = text.trim();
  if (!plain) return "";
  const lower = plain.toLowerCase();
  const anchors = snippetAnchorTokens(query);
  let idx = -1;
  for (const anchor of anchors) {
    idx = lower.indexOf(anchor.toLowerCase());
    if (idx >= 0) break;
  }
  if (idx < 0) {
    for (const token of meaningfulSearchTokens(query)) {
      idx = lower.indexOf(token);
      if (idx >= 0) break;
    }
  }
  if (idx < 0)
    return plain.slice(0, maxLen) + (plain.length > maxLen ? "…" : "");
  const start = Math.max(0, idx - 48);
  const end = Math.min(plain.length, idx + maxLen);
  let snippet = plain.slice(start, end).trim();
  if (start > 0) snippet = "…" + snippet;
  if (end < plain.length) snippet += "…";
  return snippet;
}

function localSearchArticles(
  items: SupportKnowledgeArticleRow[],
  query: string,
  mode: SearchMode,
): SupportKnowledgeSearchRow[] {
  const q = query.trim();
  if (q.length < 2) return [];
  const scored: SupportKnowledgeSearchRow[] = [];
  const lowerQuery = q.toLowerCase();
  const tokens = meaningfulSearchTokens(q);

  for (const item of items) {
    const body = item.content_preview || articlePreviewText(item);
    const tags = (item.tags || []).join(" ");
    const titleLower = item.title.toLowerCase();
    const bodyLower = body.toLowerCase();
    const tagsLower = tags.toLowerCase();
    let score = 0;
    let snippetSource = body || item.title;
    let reason = "быстрый поиск";

    if (mode === "exact") {
      if (titleLower.includes(lowerQuery)) {
        score = 1;
        snippetSource = item.title;
      } else if (bodyLower.includes(lowerQuery)) {
        score = 0.9;
      } else if (tagsLower.includes(lowerQuery)) {
        score = 0.82;
        snippetSource = tags;
      }
      reason = "точное совпадение";
    } else if (mode === "title") {
      if (titleLower.includes(lowerQuery)) {
        score = 1;
      } else if (tokens.length > 0) {
        const hits = tokens.filter((token) => titleLower.includes(token));
        score =
          hits.length > 0 ? 0.58 + (hits.length / tokens.length) * 0.36 : 0;
      }
      snippetSource = item.title;
      reason = "по названию";
    } else if (mode === "tags") {
      if (tagsLower.includes(lowerQuery)) {
        score = 1;
      } else if (tokens.length > 0) {
        const hits = tokens.filter((token) => tagsLower.includes(token));
        score =
          hits.length > 0 ? 0.55 + (hits.length / tokens.length) * 0.36 : 0;
      }
      snippetSource = tags || item.title;
      reason = "по тегам";
    } else {
      score = scoreLocalSearchMatch(item.title, body, tags, q);
    }

    if (score <= 0) continue;
    scored.push({
      ...item,
      relevance_score: score,
      match_snippet: makeLocalSnippet(snippetSource, q),
      match_reason: reason,
    });
  }

  scored.sort((a, b) => (b.relevance_score ?? 0) - (a.relevance_score ?? 0));
  return scored.slice(0, 25);
}

function readTicketKnowledgeState(
  storageKey: string,
): TicketKnowledgePanelState | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TicketKnowledgePanelState;
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeTicketKnowledgeState(
  storageKey: string,
  state: TicketKnowledgePanelState,
) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

function readFavoriteIds(storageKey: string): Set<string> {
  try {
    const raw = localStorage.getItem(storageKey);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed.filter(
        (id): id is string => typeof id === "string" && id.length > 0,
      ),
    );
  } catch {
    return new Set();
  }
}

function writeFavoriteIds(storageKey: string, favoriteIds: Set<string>) {
  try {
    localStorage.setItem(storageKey, JSON.stringify([...favoriteIds]));
  } catch {
    /* ignore */
  }
}

function isInternalMaterial(item: ListItem | null | undefined): boolean {
  if (!item) return false;
  return (
    item.material_type === "script" ||
    item.material_type === "guide" ||
    item.material_type === "instruction" ||
    item.material_type === "product_doc"
  );
}

function KnowledgeFilterButton({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count?: number;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      size="compact-xs"
      radius="md"
      variant={active ? "light" : "default"}
      color={active ? "brand" : "gray"}
      onClick={onClick}
      h={30}
      px={10}
      styles={{
        root: {
          flexShrink: 0,
          borderColor: active
            ? "var(--mantine-color-brand-outline)"
            : "var(--mantine-color-default-border)",
          background: active
            ? "var(--mantine-color-brand-light)"
            : "light-dark(var(--mantine-color-white), var(--mantine-color-dark-6))",
        },
        inner: {
          height: "100%",
        },
        label: {
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          lineHeight: 1,
          whiteSpace: "nowrap",
        },
      }}
    >
      <Text component="span" size="xs" fw={active ? 700 : 600} lh={1}>
        {label}
      </Text>
      {count != null ? (
        <Text
          component="span"
          size="xs"
          fw={700}
          c={active ? "brand" : "dimmed"}
          lh={1}
        >
          {count}
        </Text>
      ) : null}
    </Button>
  );
}

function ResultRow({
  item,
  selected,
  projectName,
  searchQuery,
  favorite,
  onToggleFavorite,
  onSelect,
}: {
  item: ListItem;
  selected: boolean;
  projectName?: string | null;
  searchQuery: string;
  favorite: boolean;
  onToggleFavorite: () => void;
  onSelect: () => void;
}) {
  const preview =
    (isSearchRow(item) && item.match_snippet) ||
    item.content_preview ||
    articlePreviewText(item).slice(0, 120);
  const typeColor = MATERIAL_TYPE_COLORS[item.material_type] || "gray";
  const highlight = searchQuery.trim().length >= 2;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onSelect}
      w="100%"
      p="sm"
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        onSelect();
      }}
      style={{
        cursor: "pointer",
        borderRadius: "var(--mantine-radius-md)",
        background: selected
          ? "linear-gradient(135deg, var(--mantine-color-brand-light), light-dark(#ffffff, var(--mantine-color-dark-6)))"
          : "light-dark(#ffffff, var(--mantine-color-dark-7))",
        border: selected
          ? "1px solid var(--mantine-color-brand-outline)"
          : "1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4))",
        boxShadow: selected
          ? "0 12px 28px light-dark(rgba(37, 99, 235, 0.13), rgba(0, 0, 0, 0.28))"
          : "0 8px 22px light-dark(rgba(15, 23, 42, 0.05), rgba(0, 0, 0, 0.18))",
        transition:
          "background 120ms ease, box-shadow 120ms ease, border-color 120ms ease",
        position: "relative",
        overflow: "hidden",
        minHeight: 112,
      }}
    >
      <Box
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          background: `var(--mantine-color-${typeColor}-5)`,
        }}
      />
      <Group
        justify="space-between"
        align="flex-start"
        gap="xs"
        wrap="nowrap"
        mb={6}
      >
        <Group gap={6} wrap="wrap" style={{ minWidth: 0 }}>
          <Badge
            size="xs"
            variant="filled"
            color={typeColor}
            style={{ flexShrink: 0 }}
          >
            {typeLabel(item)}
          </Badge>
          <Badge
            size="xs"
            variant="outline"
            color="gray"
            style={{ flexShrink: 0 }}
          >
            {scopeLabel(item, projectName)}
          </Badge>
          {isInternalMaterial(item) ? (
            <Badge
              size="xs"
              variant="light"
              color="orange"
              style={{ flexShrink: 0 }}
            >
              внутренний
            </Badge>
          ) : null}
          {isSearchRow(item) && item.relevance_score > 0 ? (
            <Badge
              size="xs"
              variant="light"
              color="green"
              style={{ flexShrink: 0 }}
            >
              {Math.round(item.relevance_score * 100)}%
            </Badge>
          ) : null}
        </Group>
        <Tooltip
          label={favorite ? "Убрать из избранного" : "В избранное"}
          withArrow
        >
          <ActionIcon
            aria-label={favorite ? "Убрать из избранного" : "В избранное"}
            variant={favorite ? "light" : "subtle"}
            color={favorite ? "yellow" : "gray"}
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite();
            }}
            style={{ flexShrink: 0 }}
          >
            <IconStar
              size={15}
              fill={favorite ? "currentColor" : "none"}
              stroke={1.6}
            />
          </ActionIcon>
        </Tooltip>
      </Group>
      {highlight ? (
        <Box
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          <HighlightedSearchText
            text={item.title}
            query={searchQuery}
            fw={selected ? 700 : 600}
            size="sm"
          />
        </Box>
      ) : (
        <Text fw={selected ? 700 : 600} size="sm" lineClamp={2}>
          {item.title}
        </Text>
      )}
      {preview ? (
        highlight ? (
          <Box
            mt={6}
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            <HighlightedSearchText
              text={preview}
              query={searchQuery}
              size="xs"
              c="dimmed"
            />
          </Box>
        ) : (
          <Text size="xs" c="dimmed" mt={6} lineClamp={3}>
            {preview}
          </Text>
        )
      ) : null}
    </Box>
  );
}

export function TicketKnowledgePanel({
  ticketId,
  operatorId,
  projectId,
  projectName,
  onInsertToReply,
  opened,
  onOpenedChange,
}: Props) {
  const setOpened = onOpenedChange;
  const [query, setQuery] = useState("");
  const immediateQuery = query.trim();
  const debouncedQuery = useDebouncedValue(immediateQuery, 160);
  const [searchMode, setSearchMode] = useState<SearchMode>("semantic");
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>("all");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [materialTypes, setMaterialTypes] = useState(FALLBACK_MATERIAL_TYPES);
  const [browseItems, setBrowseItems] = useState<SupportKnowledgeArticleRow[]>(
    [],
  );
  const [variableDefinitions, setVariableDefinitions] = useState<
    Record<string, string>
  >({});
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(() => new Set());
  const [semanticItems, setSemanticItems] = useState<
    SupportKnowledgeSearchRow[] | null
  >(null);
  const [semanticLoading, setSemanticLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [linkedHistory, setLinkedHistory] = useState<ListItem[]>([]);
  const [detail, setDetail] = useState<SupportKnowledgeArticleRow | null>(null);
  const [showFullDocument, setShowFullDocument] = useState(false);
  const [controlsCollapsed, setControlsCollapsed] = useState(true);
  const [fullDetail, setFullDetail] =
    useState<SupportKnowledgeArticleRow | null>(null);
  const [fullLoading, setFullLoading] = useState(false);
  const [smartInsertLoading, setSmartInsertLoading] = useState(false);
  const [readyReplyCopied, setReadyReplyCopied] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchAbortRef = useRef<AbortController | null>(null);
  const fullArticleRequestRef = useRef(0);
  const storageScope = `${operatorId || "anonymous"}:${ticketId || "unknown"}`;
  const favoritesStorageKey = `ticketKnowledgeFavorites:${operatorId || "anonymous"}`;
  const panelRectStorageKey = `ticketKnowledgePanelRect:${storageScope}`;
  const panelStateStorageKey = `ticketKnowledgePanelState:${storageScope}`;

  const isSearchMode = immediateQuery.length >= 2;
  const isSemanticSearchMode =
    searchMode === "semantic" && debouncedQuery.length >= 2;
  const highlightQuery = immediateQuery;

  useEffect(() => {
    setFavoriteIds(readFavoriteIds(favoritesStorageKey));
  }, [favoritesStorageKey]);

  useEffect(() => {
    let cancelled = false;
    void api
      .supportKnowledgeVariableDefinitions({ project_id: projectId })
      .then((rows: SupportKnowledgeVariableDefinitionRow[]) => {
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const row of rows) {
          const key = String(row.key || "").trim();
          const definition = String(row.definition || "").trim();
          if (key && definition) next[key] = definition;
        }
        setVariableDefinitions(next);
      })
      .catch(() => {
        if (!cancelled) setVariableDefinitions({});
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    writeFavoriteIds(favoritesStorageKey, favoriteIds);
  }, [favoritesStorageKey, favoriteIds]);

  const toggleFavorite = useCallback((articleId: string) => {
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (next.has(articleId)) next.delete(articleId);
      else next.add(articleId);
      return next;
    });
  }, []);

  const scopedBrowse = useMemo(
    () => filterByScope(browseItems, scopeFilter, favoriteIds),
    [browseItems, scopeFilter, favoriteIds],
  );

  const localSearchResults = useMemo(() => {
    if (!isSearchMode) return [];
    const typed = filterByType(
      scopedBrowse,
      typeFilter,
    ) as SupportKnowledgeArticleRow[];
    return localSearchArticles(typed, immediateQuery, searchMode);
  }, [isSearchMode, scopedBrowse, typeFilter, immediateQuery, searchMode]);

  const searchItems = useMemo(() => {
    if (!isSearchMode) return null;
    if (searchMode === "semantic" && semanticItems !== null) {
      return semanticItems.length > 0 ? semanticItems : localSearchResults;
    }
    return localSearchResults;
  }, [isSearchMode, searchMode, semanticItems, localSearchResults]);

  const rawListItems: ListItem[] = isSearchMode
    ? (searchItems ?? [])
    : browseItems;

  const scopedItems = useMemo(
    () => filterByScope(rawListItems, scopeFilter, favoriteIds),
    [rawListItems, scopeFilter, favoriteIds],
  );

  const filteredItems = useMemo(() => {
    const typed = filterByType(scopedItems, typeFilter);
    return [...typed].sort((a, b) => {
      const favoriteDelta =
        Number(favoriteIds.has(b.id)) - Number(favoriteIds.has(a.id));
      if (favoriteDelta !== 0) return favoriteDelta;
      if (isSearchMode) return 0;
      return a.title.localeCompare(b.title, "ru");
    });
  }, [scopedItems, typeFilter, favoriteIds, isSearchMode]);

  const typeCounts = useMemo(() => countByType(scopedItems), [scopedItems]);
  const generalItemsCount = useMemo(
    () => rawListItems.filter((item) => !item.project_id).length,
    [rawListItems],
  );
  const favoriteItemsCount = useMemo(
    () => rawListItems.filter((item) => favoriteIds.has(item.id)).length,
    [rawListItems, favoriteIds],
  );

  const groupedBrowse = useMemo(() => {
    if (isSearchMode || typeFilter !== "all") return null;
    const groups = new Map<string, ListItem[]>();
    for (const item of filteredItems) {
      const key = item.material_type || "other";
      const list = groups.get(key) ?? [];
      list.push(item);
      groups.set(key, list);
    }
    return materialTypes
      .filter((mt) => (groups.get(mt.key)?.length ?? 0) > 0)
      .map((mt) => ({
        type: mt,
        items: (groups.get(mt.key) ?? []).sort((a, b) =>
          a.title.localeCompare(b.title, "ru"),
        ),
      }));
  }, [filteredItems, isSearchMode, typeFilter, materialTypes]);

  const loadBrowse = useCallback(async () => {
    setListLoading(true);
    try {
      const [projectArts, generalArts] = await Promise.all([
        api
          .supportKnowledgeList({
            project_id: projectId,
            include_content: true,
          })
          .catch(() => [] as SupportKnowledgeArticleRow[]),
        api
          .supportKnowledgeList({ general_only: true, include_content: true })
          .catch(() => [] as SupportKnowledgeArticleRow[]),
      ]);
      setBrowseItems([...(generalArts || []), ...(projectArts || [])]);
    } finally {
      setListLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!opened) return;
    void loadBrowse();
    void api
      .supportKnowledgeMaterialTypes()
      .then((types) => {
        if (Array.isArray(types) && types.length > 0) {
          setMaterialTypes(types as typeof FALLBACK_MATERIAL_TYPES);
        }
      })
      .catch(() => {});
    const t = window.setTimeout(() => searchInputRef.current?.focus(), 120);
    return () => window.clearTimeout(t);
  }, [opened, loadBrowse]);

  useEffect(() => {
    if (!opened || !isSemanticSearchMode) {
      searchAbortRef.current?.abort();
      setSemanticItems(null);
      setSemanticLoading(false);
      return;
    }

    searchAbortRef.current?.abort();
    const ac = new AbortController();
    searchAbortRef.current = ac;
    setSemanticLoading(true);

    const params: Parameters<typeof api.supportKnowledgeSearch>[0] = {
      q: debouncedQuery,
      limit: 20,
      include_content: false,
      material_type: typeFilter !== "all" ? typeFilter : undefined,
    };
    if (scopeFilter === "all" || scopeFilter === "favorites") {
      params.project_id = projectId;
      params.include_general = true;
    } else {
      params.general_only = true;
    }

    void api
      .supportKnowledgeSearch(params, { signal: ac.signal })
      .then((rows) => {
        if (ac.signal.aborted) return;
        setSemanticItems(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!ac.signal.aborted) setSemanticItems(null);
      })
      .finally(() => {
        if (!ac.signal.aborted) setSemanticLoading(false);
      });

    return () => {
      ac.abort();
    };
  }, [
    opened,
    isSemanticSearchMode,
    debouncedQuery,
    projectId,
    scopeFilter,
    typeFilter,
  ]);

  useEffect(() => {
    if (!opened) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpened(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [opened]);

  const loadFullDocument = useCallback(
    async (articleId: string, options?: { show?: boolean }) => {
      const requestId = fullArticleRequestRef.current + 1;
      fullArticleRequestRef.current = requestId;
      setFullLoading(true);
      try {
        const full = await api.supportKnowledgeGet(articleId);
        if (fullArticleRequestRef.current !== requestId) return;
        setFullDetail(full);
        if (options?.show ?? true) {
          setShowFullDocument(true);
        }
      } catch (e) {
        if (fullArticleRequestRef.current === requestId) {
          showNotification({
            title: "Не удалось открыть материал",
            message: e instanceof ApiError ? e.message : "Материал удалён или недоступен",
            color: "red",
          });
        }
      } finally {
        if (fullArticleRequestRef.current === requestId) {
          setFullLoading(false);
        }
      }
    },
    [],
  );

  const selectArticle = useCallback(
    (item: ListItem, linked = false) => {
      if (!linked) setLinkedHistory([]);
      const fromSearch = isSearchRow(item);
      setSelectedId(item.id);
      setShowFullDocument(!fromSearch);
      setFullDetail(null);
      setDetail(item);
      void loadFullDocument(item.id, { show: !fromSearch });
    },
    [loadFullDocument],
  );

  const openLinkedArticle = useCallback(
    (article: SupportKnowledgeArticleRow) => {
      if (detail && detail.id !== article.id) setLinkedHistory(history => [...history, detail]);
      selectArticle(article, true);
    },
    [selectArticle, detail],
  );

  useEffect(() => {
    const saved = readTicketKnowledgeState(panelStateStorageKey);
    setSelectedId(saved?.selectedId ?? null);
    setDetail(null);
    setShowFullDocument(!!saved?.showFullDocument);
    setControlsCollapsed(saved?.controlsCollapsed ?? true);
    setFullDetail(null);
    setSemanticItems(null);
    setQuery(saved?.query ?? "");
    setSearchMode(normalizeSearchMode(saved?.searchMode));
    setScopeFilter(normalizeScopeFilter(saved?.scopeFilter));
    setTypeFilter(saved?.typeFilter ?? "all");
  }, [panelStateStorageKey, projectId]);

  useEffect(() => {
    writeTicketKnowledgeState(panelStateStorageKey, {
      query,
      searchMode,
      scopeFilter,
      typeFilter,
      selectedId,
      showFullDocument,
      controlsCollapsed,
    });
  }, [
    panelStateStorageKey,
    query,
    searchMode,
    scopeFilter,
    typeFilter,
    selectedId,
    showFullDocument,
    controlsCollapsed,
  ]);

  const resetSelectedArticle = () => {
    setLinkedHistory([]);
    setSelectedId(null);
    setDetail(null);
    setShowFullDocument(false);
    setFullDetail(null);
  };

  useEffect(() => {
    if (!opened || !isSearchMode || filteredItems.length === 0) return;
    if (!selectedId || !filteredItems.some((i) => i.id === selectedId)) {
      selectArticle(filteredItems[0]);
    }
  }, [opened, isSearchMode, filteredItems, selectedId, selectArticle]);

  useEffect(() => {
    if (!opened || !selectedId || detail?.id === selectedId) return;
    const local = browseItems.find((item) => item.id === selectedId);
    if (local) {
      const shouldShowFull = showFullDocument || !isSearchMode;
      setDetail(local);
      setShowFullDocument(shouldShowFull);
      void loadFullDocument(local.id, { show: shouldShowFull });
      return;
    }
    void api
      .supportKnowledgeGet(selectedId)
      .then((article) => {
        const shouldShowFull = showFullDocument || !isSearchMode;
        setDetail(article);
        setFullDetail(article);
        setShowFullDocument(shouldShowFull);
      })
      .catch(() => {
        setSelectedId(null);
        setShowFullDocument(false);
      });
  }, [
    opened,
    selectedId,
    detail?.id,
    browseItems,
    showFullDocument,
    isSearchMode,
    loadFullDocument,
  ]);

  const fragmentText = useMemo(() => {
    if (!detail) return "";
    if (isSearchRow(detail) && detail.match_snippet)
      return detail.match_snippet.trim();
    if (detail.content_preview) return detail.content_preview.trim();
    if (detail.content) {
      return stripKnowledgeContent(
        detail.content,
        detail.content_format ?? "html",
      ).slice(0, 320);
    }
    return "";
  }, [detail]);

  const loadReadyReplyText = async (): Promise<string> => {
    if (!detail) return "";
    const source =
      fullDetail?.id === detail.id && fullDetail.content
        ? fullDetail
        : await api.supportKnowledgeGet(detail.id);
    if (source.id !== fullDetail?.id) {
      setFullDetail(source);
    }
    const readyText = articleReadyReplyText(source);
    if (!readyText.trim()) {
      showNotification({
        title: "База знаний",
        message:
          "В выбранной статье нет блока «Готовый ответ», «Готовый текст» или FAQ-блока «Ответ».",
        color: "yellow",
      });
    }
    return readyText;
  };

  const handleInsertReadyReply = async () => {
    if (!detail || detail.material_type === "script" || smartInsertLoading)
      return;
    setSmartInsertLoading(true);
    try {
      const readyText = await loadReadyReplyText();
      if (!readyText.trim()) return;
      onInsertToReply(readyText);
      setOpened(false);
    } finally {
      setSmartInsertLoading(false);
    }
  };

  const handleCopyReadyReply = async () => {
    if (!detail || detail.material_type === "script" || smartInsertLoading)
      return;
    setSmartInsertLoading(true);
    try {
      const readyText = await loadReadyReplyText();
      if (!readyText.trim()) return;
      await navigator.clipboard.writeText(readyText);
      setReadyReplyCopied(true);
      window.setTimeout(() => setReadyReplyCopied(false), 1500);
      setOpened(false);
    } finally {
      setSmartInsertLoading(false);
    }
  };

  const searchStatus = isSearchMode
    ? searchMode === "semantic"
      ? semanticLoading
        ? semanticItems
          ? "уточняем…"
          : "ищем…"
        : semanticItems
          ? null
          : "быстрый поиск"
      : searchMode === "exact"
        ? "точная фраза"
        : searchMode === "title"
          ? "название"
          : "теги"
    : null;

  const selectedFromSearch = !!detail && isSearchRow(detail);
  const selectedIsFavorite = !!detail && favoriteIds.has(detail.id);
  const selectedIsInternal = isInternalMaterial(detail);
  const loadedSelectedArticle =
    fullDetail?.id === detail?.id ? fullDetail : null;
  const selectedReadyReplyText = loadedSelectedArticle
    ? articleReadyReplyText(loadedSelectedArticle).trim()
    : "";
  const readyReplyActionsDisabled =
    !detail ||
    detail.material_type === "script" ||
    !loadedSelectedArticle ||
    !selectedReadyReplyText;
  const readyReplyDisabledReason = !detail
    ? null
    : detail.material_type === "script"
      ? "Для скрипта оператора готовый ответ не используется"
      : !loadedSelectedArticle
        ? "Материал еще загружается"
        : !selectedReadyReplyText
          ? "В материале нет готового ответа для вставки"
          : null;

  const renderList = () => {
    if ((listLoading || semanticLoading) && filteredItems.length === 0) {
      return <PageCenterLoader />;
    }
    if (filteredItems.length === 0) {
      return (
        <Text size="sm" c="dimmed" ta="center" py="lg" px="sm">
          {isSearchMode
            ? "Ничего не найдено — попробуйте другой запрос или смените фильтры"
            : "В выбранном разделе пока нет материалов"}
        </Text>
      );
    }

    if (groupedBrowse) {
      return (
        <Stack gap="md">
          {groupedBrowse.map(({ type, items }) => (
            <Box key={type.key}>
              <Group gap={6} px={4} mb={4} wrap="nowrap">
                <Badge
                  size="sm"
                  variant="light"
                  color={MATERIAL_TYPE_COLORS[type.key] || "gray"}
                >
                  {type.label}
                </Badge>
                <Text size="xs" c="dimmed">
                  {items.length}
                </Text>
              </Group>
              <Stack gap={4}>
                {items.map((item) => (
                  <ResultRow
                    key={item.id}
                    item={item}
                    selected={item.id === selectedId}
                    projectName={projectName}
                    searchQuery={highlightQuery}
                    favorite={favoriteIds.has(item.id)}
                    onToggleFavorite={() => toggleFavorite(item.id)}
                    onSelect={() => selectArticle(item)}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      );
    }

    const sorted = isSearchMode
      ? [...filteredItems]
      : [...filteredItems].sort((a, b) => a.title.localeCompare(b.title, "ru"));

    return (
      <Stack gap={4}>
        {sorted.map((item) => (
          <ResultRow
            key={item.id}
            item={item}
            selected={item.id === selectedId}
            projectName={projectName}
            searchQuery={highlightQuery}
            favorite={favoriteIds.has(item.id)}
            onToggleFavorite={() => toggleFavorite(item.id)}
            onSelect={() => selectArticle(item)}
          />
        ))}
      </Stack>
    );
  };

  return (
    <>
      {opened ? (
        <FloatingWorkspacePanel
          storageKey={panelRectStorageKey}
          opened={opened}
          onClose={() => setOpened(false)}
          defaultWidth={1040}
          defaultHeight={720}
          minWidth={620}
          minHeight={460}
          icon={
            <Box
              style={{
                width: 38,
                height: 38,
                borderRadius: "var(--mantine-radius-lg)",
                display: "grid",
                placeItems: "center",
                color: "var(--mantine-color-brand-6)",
                background: "var(--mantine-color-brand-light)",
              }}
            >
              <IconBook size={21} stroke={1.7} />
            </Box>
          }
          title={
            <Group gap={8} wrap="nowrap">
              <Text fw={700} size="md" lineClamp={1}>
                База знаний
              </Text>
              <Badge size="sm" variant="light" color="gray">
                {browseItems.length}
              </Badge>
            </Group>
          }
          subtitle={
            <>
              Найдите материал и вставьте только готовый ответ в обращение
              {projectName ? ` · ${projectName}` : ""}
            </>
          }
          actions={
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={() => setOpened(false)}
              aria-label="Закрыть"
            >
              <IconX size={18} stroke={1.5} />
            </ActionIcon>
          }
        >
          <Box
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              background:
                "linear-gradient(180deg, light-dark(rgba(255,255,255,0.88), rgba(26,27,30,0.7)), light-dark(#f1f5f9, var(--mantine-color-dark-8)))",
            }}
          >
            <Box
              p="sm"
              style={{
                flex: "0 0 auto",
                borderBottom: "1px solid var(--mantine-color-default-border)",
                background:
                  "light-dark(rgba(248,250,252,0.78), rgba(26,27,30,0.7))",
              }}
            >
              <Group justify="space-between" gap="sm" wrap="nowrap">
                <Button
                  variant="subtle"
                  size="xs"
                  radius="md"
                  leftSection={<IconSearch size={14} stroke={1.6} />}
                  rightSection={
                    controlsCollapsed ? (
                      <IconChevronDown size={14} stroke={1.6} />
                    ) : (
                      <IconChevronUp size={14} stroke={1.6} />
                    )
                  }
                  onClick={() => setControlsCollapsed((value) => !value)}
                >
                  Поиск и фильтры
                </Button>
                <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                  {query ? (
                    <Badge
                      size="sm"
                      variant="light"
                      color="brand"
                      style={{ maxWidth: 220 }}
                    >
                      <Text size="xs" truncate>
                        {query}
                      </Text>
                    </Badge>
                  ) : null}
                  <Badge
                    size="sm"
                    variant="light"
                    color={filteredItems.length ? "brand" : "gray"}
                  >
                    {filteredItems.length}
                  </Badge>
                </Group>
              </Group>

              <Collapse in={!controlsCollapsed}>
                <Paper
                  withBorder
                  radius="lg"
                  p="xs"
                  mt="xs"
                  style={{
                    background:
                      "light-dark(rgba(255,255,255,0.88), rgba(26,27,30,0.82))",
                  }}
                >
                  <Stack gap={6}>
                    <SegmentedControl
                      size="xs"
                      radius="md"
                      fullWidth
                      value={searchMode}
                      onChange={(value) => {
                        setSearchMode(value as SearchMode);
                        setSemanticItems(null);
                        resetSelectedArticle();
                      }}
                      data={[
                        { value: "semantic", label: "Смысловой" },
                        { value: "exact", label: "Точная фраза" },
                        { value: "title", label: "Название" },
                        { value: "tags", label: "Теги" },
                      ]}
                    />
                    <TextInput
                      ref={searchInputRef}
                      size="sm"
                      radius="md"
                      placeholder="Поиск по базе знаний…"
                      leftSection={<IconSearch size={15} stroke={1.6} />}
                      value={query}
                      onChange={(e) => setQuery(e.currentTarget.value)}
                      rightSectionWidth={searchStatus ? 108 : undefined}
                      rightSection={
                        searchStatus ? (
                          <Badge
                            size="xs"
                            variant="light"
                            color={semanticLoading ? "blue" : "gray"}
                          >
                            {searchStatus}
                          </Badge>
                        ) : null
                      }
                      styles={{
                        input: {
                          minHeight: 36,
                          fontSize: "14px",
                        },
                      }}
                    />

                    <ScrollArea type="auto" offsetScrollbars scrollbars="x">
                      <Group
                        gap={8}
                        wrap="nowrap"
                        align="center"
                        pb={2}
                        style={{ minWidth: "max-content" }}
                      >
                        <Group
                          gap={4}
                          wrap="nowrap"
                          align="center"
                          p={3}
                          style={{
                            flexShrink: 0,
                            borderRadius: "var(--mantine-radius-lg)",
                            border:
                              "1px solid var(--mantine-color-default-border)",
                            background:
                              "light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-7))",
                          }}
                        >
                          <KnowledgeFilterButton
                            active={scopeFilter === "all"}
                            label="Все материалы"
                            count={rawListItems.length}
                            onClick={() => {
                              setScopeFilter("all");
                              resetSelectedArticle();
                            }}
                          />
                          <KnowledgeFilterButton
                            active={scopeFilter === "general"}
                            label="Общие"
                            count={generalItemsCount}
                            onClick={() => {
                              setScopeFilter("general");
                              resetSelectedArticle();
                            }}
                          />
                          <KnowledgeFilterButton
                            active={scopeFilter === "favorites"}
                            label="Избранное"
                            count={favoriteItemsCount}
                            onClick={() => {
                              setScopeFilter("favorites");
                              resetSelectedArticle();
                            }}
                          />
                        </Group>

                        <Box
                          aria-hidden
                          style={{
                            width: 1,
                            height: 24,
                            flexShrink: 0,
                            background: "var(--mantine-color-default-border)",
                          }}
                        />

                        <Group
                          gap={4}
                          wrap="nowrap"
                          align="center"
                          p={3}
                          style={{
                            flexShrink: 0,
                            borderRadius: "var(--mantine-radius-lg)",
                            border:
                              "1px solid var(--mantine-color-default-border)",
                            background:
                              "light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-7))",
                          }}
                        >
                          <KnowledgeFilterButton
                            active={typeFilter === "all"}
                            label="Все типы"
                            count={scopedItems.length}
                            onClick={() => {
                              setTypeFilter("all");
                              resetSelectedArticle();
                            }}
                          />
                          {materialTypes.map((mt) => {
                            const count = typeCounts.get(mt.key) ?? 0;
                            if (count === 0 && typeFilter !== mt.key)
                              return null;
                            return (
                              <KnowledgeFilterButton
                                key={mt.key}
                                active={typeFilter === mt.key}
                                label={mt.label}
                                count={count}
                                onClick={() => {
                                  setTypeFilter(mt.key);
                                  resetSelectedArticle();
                                }}
                              />
                            );
                          })}
                        </Group>
                      </Group>
                    </ScrollArea>
                  </Stack>
                </Paper>
              </Collapse>
            </Box>

            <Box
              style={{
                flex: 1,
                minHeight: 0,
                padding:
                  "0 var(--mantine-spacing-sm) var(--mantine-spacing-sm)",
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                gridTemplateRows: "minmax(0, 1fr)",
                gap: 14,
                alignItems: "stretch",
                overflow: "hidden",
              }}
            >
              <Paper
                withBorder
                radius="lg"
                style={{
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  background:
                    "light-dark(rgba(255,255,255,0.92), var(--mantine-color-dark-7))",
                  height: "100%",
                }}
              >
                <Group
                  justify="space-between"
                  gap="sm"
                  p="md"
                  pb="sm"
                  wrap="nowrap"
                  style={{
                    borderBottom:
                      "1px solid var(--mantine-color-default-border)",
                  }}
                >
                  <Box style={{ minWidth: 0 }}>
                    <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                      3. Материалы
                    </Text>
                    <Text size="sm" fw={700} lineClamp={1}>
                      {isSearchMode
                        ? "Результаты поиска"
                        : groupedBrowse
                          ? "Каталог по разделам"
                          : "Список статей"}
                    </Text>
                  </Box>
                  <Badge
                    variant="light"
                    color={filteredItems.length ? "brand" : "gray"}
                    size="lg"
                  >
                    {filteredItems.length}
                  </Badge>
                </Group>
                <ScrollArea
                  style={{ flex: 1, minHeight: 0 }}
                  offsetScrollbars
                  type="auto"
                  p="md"
                >
                  {renderList()}
                </ScrollArea>
              </Paper>

              <Paper
                withBorder
                radius="lg"
                style={{
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  background:
                    "light-dark(#ffffff, var(--mantine-color-dark-7))",
                  boxShadow:
                    "0 18px 50px light-dark(rgba(15, 23, 42, 0.08), rgba(0, 0, 0, 0.25))",
                  height: "100%",
                }}
              >
                <Box
                  p="md"
                  pb="sm"
                  style={{
                    borderBottom:
                      "1px solid var(--mantine-color-default-border)",
                  }}
                >
                  <Group
                    justify="space-between"
                    align="flex-start"
                    gap="sm"
                    wrap="nowrap"
                  >
                    <Box style={{ minWidth: 0, flex: 1 }}>
                      <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={6}>
                        4. Ответ оператору
                      </Text>
                      {detail ? (
                        <>
                          <Group gap={6} mb={8} wrap="wrap">
                            <Badge
                              variant="filled"
                              color={
                                MATERIAL_TYPE_COLORS[detail.material_type] ||
                                "gray"
                              }
                            >
                              {typeLabel(detail)}
                            </Badge>
                            <Badge variant="outline" color="gray">
                              {scopeLabel(detail, projectName)}
                            </Badge>
                            {isSearchRow(detail) && detail.match_reason ? (
                              <Badge size="xs" variant="dot" color="teal">
                                {detail.match_reason}
                              </Badge>
                            ) : null}
                          </Group>
                          <Text fw={800} size="md" lineClamp={3}>
                            {isSearchMode && highlightQuery.length >= 2 ? (
                              <HighlightedSearchText
                                text={detail.title}
                                query={highlightQuery}
                                fw={800}
                                size="md"
                              />
                            ) : (
                              detail.title
                            )}
                          </Text>
                        </>
                      ) : (
                        <Text size="sm" c="dimmed">
                          Выберите материал слева
                        </Text>
                      )}
                    </Box>
                    {detail ? (
                      <Tooltip
                        label={
                          selectedIsFavorite
                            ? "Убрать из избранного"
                            : "В избранное"
                        }
                        withArrow
                      >
                        <ActionIcon
                          aria-label={
                            selectedIsFavorite
                              ? "Убрать из избранного"
                              : "В избранное"
                          }
                          variant={selectedIsFavorite ? "light" : "default"}
                          color={selectedIsFavorite ? "yellow" : "gray"}
                          size="lg"
                          onClick={() => toggleFavorite(detail.id)}
                          style={{ flexShrink: 0 }}
                        >
                          <IconStar
                            size={18}
                            fill={selectedIsFavorite ? "currentColor" : "none"}
                            stroke={1.6}
                          />
                        </ActionIcon>
                      </Tooltip>
                    ) : null}
                  </Group>
                </Box>

                {selectedIsInternal ? (
                  <Box px="md" pt="sm" style={{ flex: "0 0 auto" }}>
                    <Paper
                      withBorder
                      radius="md"
                      px="sm"
                      py={8}
                      style={{
                        borderColor: "var(--mantine-color-orange-outline)",
                        background:
                          "light-dark(var(--mantine-color-orange-0), rgba(245, 159, 0, 0.13))",
                      }}
                    >
                      <Text size="sm" fw={700} c="orange.8">
                        Внутренний материал. Не отправлять клиенту полностью.
                      </Text>
                    </Paper>
                  </Box>
                ) : null}

                <ScrollArea
                  style={{ flex: 1, minHeight: 0 }}
                  offsetScrollbars
                  type="auto"
                  p="md"
                >
                  {detail ? (
                    fullLoading && showFullDocument ? (
                      <PageCenterLoader />
                    ) : showFullDocument && fullDetail ? (
                      <>
                      {linkedHistory.length > 0 && <Button variant="subtle" onClick={() => {
                        const previous = linkedHistory[linkedHistory.length - 1];
                        setLinkedHistory(history => history.slice(0, -1));
                        selectArticle(previous, true);
                      }}>Назад к предыдущему материалу</Button>}
                      <KnowledgeArticleContent
                        content={fullDetail.content ?? ""}
                        projectId={fullDetail.project_id}
                        contentFormat={fullDetail.content_format ?? "html"}
                        relatedArticles={browseItems}
                        onArticleClick={openLinkedArticle}
                        variableDefinitions={variableDefinitions}
                      />
                      </>
                    ) : (
                      <Paper
                        withBorder
                        p="md"
                        radius="lg"
                        style={{
                          background:
                            "light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))",
                          borderStyle: "dashed",
                        }}
                      >
                        <Group justify="space-between" gap="sm" mb={8}>
                          <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                            {isSearchMode
                              ? "Фрагмент совпадения"
                              : "Превью материала"}
                          </Text>
                          {selectedFromSearch ? (
                            <Badge size="xs" variant="light" color="gray">
                              результат поиска
                            </Badge>
                          ) : null}
                        </Group>
                        <HighlightedSearchText
                          text={
                            fragmentText ||
                            "Фрагмент недоступен — откройте полный документ."
                          }
                          query={highlightQuery}
                          size="sm"
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            lineHeight: 1.55,
                          }}
                        />
                      </Paper>
                    )
                  ) : (
                    <Text size="sm" c="dimmed" ta="center" py="xl">
                      Материал не выбран
                    </Text>
                  )}
                </ScrollArea>

                {showFullDocument &&
                fullDetail &&
                (fullDetail.attachments?.length ?? 0) > 0 ? (
                  <Box
                    px="md"
                    pb="sm"
                    style={{
                      flex: "0 0 auto",
                      maxHeight: "min(22vh, 180px)",
                      overflow: "auto",
                      borderTop:
                        "1px solid var(--mantine-color-default-border)",
                    }}
                  >
                    <KnowledgeArticleAttachments
                      articleId={fullDetail.id}
                      attachments={fullDetail.attachments || []}
                      pendingFiles={[]}
                      canEdit={false}
                      onAttachmentsChange={() => {}}
                      onPendingChange={() => {}}
                    />
                  </Box>
                ) : null}
              </Paper>
            </Box>

            <Group
              gap="xs"
              p="md"
              pt="sm"
              wrap="wrap"
              style={{
                flex: "0 0 auto",
                borderTop: "1px solid var(--mantine-color-default-border)",
                background:
                  "light-dark(rgba(248,250,252,0.96), rgba(26,27,30,0.92))",
                boxShadow:
                  "0 -8px 22px light-dark(rgba(15, 23, 42, 0.05), rgba(0, 0, 0, 0.16))",
              }}
            >
              <Button
                size="md"
                radius="lg"
                variant={selectedIsFavorite ? "light" : "default"}
                color={selectedIsFavorite ? "yellow" : "gray"}
                leftSection={
                  <IconStar
                    size={15}
                    fill={selectedIsFavorite ? "currentColor" : "none"}
                    stroke={1.6}
                  />
                }
                disabled={!detail}
                onClick={() => {
                  if (detail) toggleFavorite(detail.id);
                }}
              >
                {selectedIsFavorite ? "В избранном" : "В избранное"}
              </Button>
              <Tooltip
                label={readyReplyDisabledReason || ""}
                disabled={!readyReplyDisabledReason}
                withArrow
              >
                <Box component="span">
                  <Button
                    size="md"
                    radius="lg"
                    onClick={() => void handleInsertReadyReply()}
                    loading={smartInsertLoading}
                    disabled={readyReplyActionsDisabled || fullLoading}
                  >
                    Готовый ответ
                  </Button>
                </Box>
              </Tooltip>
              <Tooltip
                label={readyReplyDisabledReason || ""}
                disabled={!readyReplyDisabledReason}
                withArrow
              >
                <Box component="span">
                  <Button
                    size="md"
                    radius="lg"
                    variant="light"
                    leftSection={<IconCopy size={15} />}
                    onClick={() => void handleCopyReadyReply()}
                    disabled={
                      readyReplyActionsDisabled ||
                      fullLoading ||
                      smartInsertLoading
                    }
                  >
                    {readyReplyCopied ? "Скопировано" : "Копировать"}
                  </Button>
                </Box>
              </Tooltip>
              {selectedFromSearch && !showFullDocument ? (
                <Button
                  size="md"
                  radius="lg"
                  variant="subtle"
                  leftSection={<IconExternalLink size={15} />}
                  loading={fullLoading}
                  disabled={!detail}
                  onClick={() => {
                    if (detail) void loadFullDocument(detail.id);
                  }}
                >
                  Полный документ
                </Button>
              ) : selectedFromSearch && showFullDocument ? (
                <Button
                  size="md"
                  radius="lg"
                  variant="subtle"
                  disabled={!detail}
                  onClick={() => {
                    setShowFullDocument(false);
                  }}
                >
                  К фрагменту
                </Button>
              ) : null}
            </Group>
          </Box>
        </FloatingWorkspacePanel>
      ) : null}
    </>
  );
}
